import { authFetch, clearToken } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Target, ChevronRight, Check, Clock, Zap, Brain, 
  MapPin, Timer, TrendingUp, Star, Play, X, Gift,
  Mail, Eye, EyeOff, ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { safeSet } from "../utils/safeStorage";

const API = window.location.origin;

// Direct HTTP request that bypasses fetch interceptors (e.g. platform monitoring scripts)
// This prevents "body already consumed" errors on auth endpoints
function authRequest(url, body) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.timeout = 15000;
    xhr.onload = function () {
      let data = null;
      try { data = JSON.parse(xhr.responseText); } catch (e) { console.warn("Auth response parse error:", e); }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = function () { reject(new Error("Network error")); };
    xhr.ontimeout = function () { reject(new Error("Request timed out")); };
    xhr.send(JSON.stringify(body));
  });
}

// Retry wrapper for authRequest
async function authRequestWithRetry(url, body, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try {
      return await authRequest(url, body);
    } catch (err) {
      if (i === retries) throw err;
      await new Promise(r => setTimeout(r, 500 * (i + 1)));
    }
  }
}

export default function Landing() {
  const navigate = useNavigate();
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasShownExitPopup, setHasShownExitPopup] = useState(false);
  const [authMode, setAuthMode] = useState(null); // null | 'login' | 'register' | 'forgot' | 'reset' | 'create-password'
  const [authForm, setAuthForm] = useState({ email: "", password: "", name: "", resetCode: "", newPassword: "", inviteCode: "" });
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(true);
  const [loginError, setLoginError] = useState(null); // null | { type, message }

  const handleGoogleLogin = async () => {
    try {
      const redirectUri = `${window.location.origin}/auth/callback`;
      const response = await fetch(`${API}/api/auth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}`);
      const data = await response.json();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        setGoogleAvailable(false);
        toast.error("Google sign-in unavailable. Use email instead.");
      }
    } catch (error) {
      setGoogleAvailable(false);
      toast.error("Google sign-in unavailable. Use email instead.");
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setLoginError(null);
    try {
      const { ok, status, data } = await authRequestWithRetry(`${API}/api/auth/login`, {
        email: authForm.email, password: authForm.password
      });
      
      if (ok && data?.token) {
        safeSet("gfp_token", data.token);
        toast.success("Welcome back!");
        navigate(data.onboarding_complete ? "/dashboard" : "/onboarding", { state: { user: data } });
      } else {
        const detail = (data?.detail || "").toLowerCase();
        if (detail.includes("no_account") || detail.includes("no account found")) {
          setLoginError({ type: "no_account", message: "No account found with this email." });
        } else if (detail.includes("no password")) {
          setLoginError({ type: "no_password", message: "This account was created with Google and doesn't have a password yet." });
        } else if (status === 429 || detail.includes("too many")) {
          setLoginError({ type: "rate_limit", message: "Too many attempts. Please wait a few minutes and try again." });
        } else {
          setLoginError({ type: "wrong_password", message: "Incorrect password. Please try again." });
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      toast.error("Connection failed. Check your internet and try again.");
    }
    setAuthLoading(false);
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const { ok, data } = await authRequestWithRetry(`${API}/api/auth/register`, {
        email: authForm.email, password: authForm.password, name: authForm.name, invite_code: authForm.inviteCode
      });
      
      if (ok && data?.token) {
        safeSet("gfp_token", data.token);
        toast.success("Account created!");
        navigate("/onboarding", { state: { user: data } });
      } else {
        toast.error(data?.detail || "Registration failed");
      }
    } catch (err) {
      console.error("Register error:", err);
      toast.error("Connection failed. Check your internet and try again.");
    }
    setAuthLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const { ok } = await authRequestWithRetry(`${API}/api/auth/forgot-password`, { email: authForm.email });
      if (ok) {
        toast.success("If an account exists, a reset code has been sent to your email.");
        setAuthMode("reset");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    }
    setAuthLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const { ok, data } = await authRequestWithRetry(`${API}/api/auth/reset-password`, {
        token: authForm.resetCode, password: authForm.newPassword
      });
      if (ok) {
        toast.success("Password reset! You can now log in.");
        setAuthMode("login");
        setAuthForm(prev => ({ ...prev, password: "", resetCode: "", newPassword: "" }));
      } else {
        toast.error(data?.detail || "Reset failed");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    }
    setAuthLoading(false);
  };

  // Direct password creation for Google-only users (no token needed)
  const handleCreatePassword = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const { ok, data } = await authRequestWithRetry(`${API}/api/auth/create-password`, {
        email: authForm.email, password: authForm.newPassword, invite_code: authForm.inviteCode
      });
      if (ok) {
        toast.success("Password created! You're now logged in.");
        navigate("/dashboard", { state: { user: data } });
      } else {
        toast.error(data?.detail || "Failed to create password");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    }
    setAuthLoading(false);
  };

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShownExitPopup) {
        setShowExitPopup(true);
        setHasShownExitPopup(true);
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShownExitPopup]);

  // Auth modal — EMAIL ONLY, no Google during beta freeze
  const renderAuthModal = () => {
    if (!authMode) return null;
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setAuthMode(null)}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-200 rounded-2xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="auth-modal"
          >
            <div className="flex items-center justify-between mb-5">
              {(authMode === "forgot" || authMode === "reset") ? (
                <button onClick={() => { setAuthMode("login"); setLoginError(null); }} className="text-[#555] hover:text-[#111]">
                  <ArrowLeft className="w-5 h-5" />
                </button>
              ) : <div />}
              <h2 className="text-[#111] font-bold text-lg" data-testid="auth-modal-title">
                {authMode === "login" ? "Sign In" :
                 authMode === "register" ? "Create Account" :
                 authMode === "forgot" ? "Reset Password" :
                 "Enter Reset Code"}
              </h2>
              <button onClick={() => { setAuthMode(null); setLoginError(null); }} className="text-[#555] hover:text-[#111]">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Beta notice */}
            {(authMode === "login" || authMode === "register") && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 mb-4 text-center" data-testid="auth-beta-notice">
                <p className="text-emerald-700 text-xs font-medium">Beta — free access for all testers</p>
              </div>
            )}

            {/* SIGN IN */}
            {authMode === "login" && (
              <form onSubmit={handleEmailLogin} className="space-y-3">
                <input type="email" placeholder="Email" required value={authForm.email}
                  onChange={(e) => { setAuthForm(prev => ({ ...prev, email: e.target.value })); setLoginError(null); }}
                  className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-[#111] text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                  data-testid="login-email-input" autoComplete="email" />
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} placeholder="Password" required value={authForm.password}
                    onChange={(e) => { setAuthForm(prev => ({ ...prev, password: e.target.value })); setLoginError(null); }}
                    className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-[#111] text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500 pr-10"
                    data-testid="login-password-input" autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-[#555]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {loginError && (
                  <div data-testid="login-error-msg">
                    <p className="text-red-400 text-sm">{loginError.message}</p>
                    {loginError.type === "no_account" && (
                      <button type="button" onClick={() => { setAuthMode("register"); setLoginError(null); }}
                        className="mt-2 w-full bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-700 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors"
                        data-testid="create-account-from-error">
                        Create Account with this Email
                      </button>
                    )}
                    {loginError.type === "no_password" && (
                      <button type="button" onClick={() => { setAuthMode("create-password"); setLoginError(null); }}
                        className="mt-2 w-full bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-700 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors"
                        data-testid="create-password-from-error">
                        Create a Password for this Account
                      </button>
                    )}
                  </div>
                )}

                <Button type="submit" disabled={authLoading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-5"
                  data-testid="login-submit-btn">
                  {authLoading ? "Signing in..." : "Sign In"}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button type="button" onClick={() => { setAuthMode("forgot"); setLoginError(null); }}
                    className="text-[#555] hover:text-emerald-700" data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                  <button type="button" onClick={() => { setAuthMode("register"); setLoginError(null); }}
                    className="text-emerald-700 hover:text-emerald-700" data-testid="switch-to-register">
                    Create account
                  </button>
                </div>
              </form>
            )}

            {/* CREATE ACCOUNT */}
            {authMode === "register" && (
              <form onSubmit={handleEmailRegister} className="space-y-3">
                <input type="text" placeholder="Full Name" required value={authForm.name}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-[#111] text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                  data-testid="register-name-input" autoComplete="name" />
                <input type="email" placeholder="Email" required value={authForm.email}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-[#111] text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                  data-testid="register-email-input" autoComplete="email" />
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} placeholder="Password (8+ chars, upper, lower, digit)" required value={authForm.password}
                    onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-[#111] text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500 pr-10"
                    data-testid="register-password-input" autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-[#555]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button type="submit" disabled={authLoading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-5"
                  data-testid="register-submit-btn">
                  {authLoading ? "Creating account..." : "Create Account"}
                </Button>
                <p className="text-center text-sm">
                  <span className="text-[#555]">Already have an account? </span>
                  <button type="button" onClick={() => setAuthMode("login")} className="text-emerald-700 hover:text-emerald-700"
                    data-testid="switch-to-login">Sign in</button>
                </p>
              </form>
            )}

            {/* FORGOT PASSWORD */}
            {authMode === "forgot" && (
              <form onSubmit={handleForgotPassword} className="space-y-3">
                <p className="text-[#555] text-sm mb-2">Enter your email and we'll send a reset code.</p>
                <input type="email" placeholder="Email" required value={authForm.email}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-[#111] text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                  data-testid="forgot-email-input" />
                <Button type="submit" disabled={authLoading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-5"
                  data-testid="forgot-submit-btn">
                  {authLoading ? "Sending..." : "Send Reset Code"}
                </Button>
              </form>
            )}

            {/* RESET PASSWORD */}
            {authMode === "reset" && (
              <form onSubmit={handleResetPassword} className="space-y-3">
                <p className="text-[#555] text-sm mb-2">Enter the code from your email and a new password.</p>
                <input type="text" placeholder="Reset Code" required value={authForm.resetCode}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, resetCode: e.target.value }))}
                  className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-[#111] text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500 tracking-widest font-mono"
                  data-testid="reset-code-input" />
                <input type="password" placeholder="New Password (8+ chars)" required value={authForm.newPassword}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-[#111] text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                  data-testid="reset-password-input" />
                <Button type="submit" disabled={authLoading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-5"
                  data-testid="reset-submit-btn">
                  {authLoading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-50/90 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#111]" />
            </div>
            <span className="font-bold text-xl text-[#111] tracking-tight">GolfFlowPro</span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              data-testid="header-register-btn"
              onClick={() => setAuthMode("register")}
              className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm"
            >
              Get Started
            </Button>
            <Button 
              data-testid="header-login-btn"
              onClick={() => setAuthMode("login")}
              variant="ghost"
              className="text-[#555] hover:text-[#111] hover:bg-gray-200 text-sm"
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/20 via-[#0a0f0d] to-[#0a0f0d]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-emerald-500/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 mb-6 text-sm px-4 py-1.5">
              For Serious Golfers • Handicap 0-20
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111] mb-6 tracking-tight leading-tight">
              Your Personal<br />
              <span className="text-emerald-700">Practice Session</span><br />
              Every Time
            </h1>
            
            <p className="text-lg md:text-xl text-[#555] mb-8 max-w-2xl mx-auto leading-relaxed">
              Your personal practice session every time.<br />
              <span className="text-[#111] font-medium">Zero decisions. Maximum improvement.</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                data-testid="hero-cta-btn"
                onClick={() => setAuthMode("register")}
                size="lg"
                className="rounded-full px-8 py-6 text-lg font-semibold bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25"
              >
                Start Your First Session
                <Play className="ml-2 w-5 h-5" />
              </Button>
              <Button
                data-testid="hero-email-btn"
                onClick={() => setAuthMode("login")}
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 text-lg border-white/20 text-[#111] hover:bg-gray-200"
              >
                <Mail className="mr-2 w-5 h-5" />
                Continue with Email
              </Button>
            </div>
            
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-5 py-2" data-testid="beta-banner">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-emerald-700 text-sm font-medium">Beta — Full Free Access for All Testers</span>
            </div>
          </motion.div>

          {/* Session Preview Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12"
          >
            <Card className="bg-white border-gray-200 max-w-md mx-auto overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-emerald-500/10 px-6 py-4 border-b border-gray-200">
                  <p className="text-emerald-700 text-sm font-medium uppercase tracking-wider">Today's Session</p>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#111] mb-2">Short Game Precision</h3>
                  <p className="text-[#555] text-sm mb-4">Focus: Distance control & green reading</p>
                  <div className="flex items-center gap-4 text-sm text-[#555] mb-6">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      <span>25 min</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" />
                      <span>Practice Green</span>
                    </div>
                  </div>
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
                    <Play className="w-4 h-4 mr-2" />
                    Start Session
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#111] mb-4">
              Elite Practice, Simplified
            </h2>
            <p className="text-[#555] text-lg max-w-2xl mx-auto">
              No more standing on the range wondering what to work on
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "1", icon: <Clock className="w-6 h-6" />, title: "Set Your Time", desc: "10 min to 4 hours. We adapt." },
              { step: "2", icon: <MapPin className="w-6 h-6" />, title: "Pick Location", desc: "Range, green, home, or course." },
              { step: "3", icon: <Target className="w-6 h-6" />, title: "Get Your Session", desc: "AI-built for your weaknesses." },
              { step: "4", icon: <Play className="w-6 h-6" />, title: "Press Start", desc: "Follow the guided session." }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4 text-emerald-700">
                  {item.icon}
                </div>
                <div className="w-8 h-8 rounded-full bg-gray-200 text-[#111] flex items-center justify-center mx-auto mb-3 font-bold text-sm">
                  {item.step}
                </div>
                <h3 className="font-semibold text-[#111] mb-2">{item.title}</h3>
                <p className="text-[#555] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Session Structure */}
      <section className="py-24 px-6 bg-[#111815]">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="bg-gray-200 text-[#555] border-gray-200 mb-4">
              Science-Backed Structure
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[#111] mb-4">
              Every Session, Perfectly Structured
            </h2>
            <p className="text-[#555] text-lg max-w-2xl mx-auto">
              Based on motor learning research for maximum skill transfer
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: "Calibration", time: "3 min", desc: "Perception & feel warm-up", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
              { name: "Primary Skill", time: "10 min", desc: "Deep work on your weakness", color: "bg-emerald-500/20 text-emerald-700 border-emerald-500/30" },
              { name: "Random Transfer", time: "8 min", desc: "Simulate on-course chaos", color: "bg-amber-500/20 text-[#111] border-amber-500/30" },
              { name: "Pressure Test", time: "4 min", desc: "Consequence-based reps", color: "bg-red-500/20 text-red-400 border-red-500/30" }
            ].map((block, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-gray-50 border-gray-200 h-full">
                  <CardContent className="p-5">
                    <Badge className={`${block.color} mb-3`}>{block.time}</Badge>
                    <h3 className="font-semibold text-[#111] mb-1">{block.name}</h3>
                    <p className="text-[#555] text-sm">{block.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#111] mb-4">
              Built for Real Golfers
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Timer className="w-6 h-6" />, title: "Flexible Time", desc: "10 minutes or 4 hours. The engine adapts your session perfectly." },
              { icon: <Brain className="w-6 h-6" />, title: "Coach Integration", desc: "Add drills from your coach. We'll fit them into your sessions." },
              { icon: <MapPin className="w-6 h-6" />, title: "Any Environment", desc: "Range, practice green, home mat, or on-course structured practice." },
              { icon: <Zap className="w-6 h-6" />, title: "Zero Decisions", desc: "No drill browsing. No complexity. Just follow the session." },
              { icon: <TrendingUp className="w-6 h-6" />, title: "Smart Adaptation", desc: "Quick feedback after each drill. The AI learns your patterns." },
              { icon: <Target className="w-6 h-6" />, title: "Pressure Training", desc: "Every session ends with a consequence-based performance task." }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="bg-white border-gray-200 h-full hover:border-emerald-500/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-700 mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="font-semibold text-[#111] mb-2">{feature.title}</h3>
                    <p className="text-[#555] text-sm leading-relaxed">{feature.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-6 bg-[#111815]">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#111] mb-4">
              Simple Pricing
            </h2>
            <p className="text-[#555]">Start free. Upgrade when you're ready.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Free */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gray-50 border-gray-200 h-full">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-[#111] mb-2">Free</h3>
                  <div className="text-4xl font-bold text-[#111] mb-1">€0</div>
                  <p className="text-[#555] text-sm mb-6">Try it out</p>
                  <ul className="space-y-3 mb-8">
                    {["1 practice session", "Basic session structure", "All skill areas"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#555]">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => setAuthMode("register")}
                    variant="outline" 
                    className="w-full border-white/20 text-[#111] hover:bg-gray-200"
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Pro */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-emerald-500/10 border-emerald-500/30 h-full relative overflow-hidden">
                <div className="absolute top-4 right-4">
                  <Badge className="bg-emerald-500 text-[#111]">7-Day Free Trial</Badge>
                </div>
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-[#111] mb-2">Pro</h3>
                  <div className="text-4xl font-bold text-emerald-700 mb-1">€9.99</div>
                  <p className="text-[#555] text-sm mb-6">per month</p>
                  <ul className="space-y-3 mb-8">
                    {[
                      "Unlimited daily sessions",
                      "Coach drill integration",
                      "On-course practice mode",
                      "Performance tracking",
                      "Intelligent adaptation",
                      "Pressure challenges"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#555]">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => setAuthMode("register")}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    Start 7-Day Free Trial
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 bg-gray-50">
        <motion.div 
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#111] mb-4">
            Stop Guessing.<br />Start Improving.
          </h2>
          <p className="text-[#555] text-lg mb-8 max-w-xl mx-auto">
            Join serious golfers who practice with purpose. Your next session is waiting.
          </p>
          <Button 
            data-testid="final-cta-btn"
            onClick={() => setAuthMode("register")}
            size="lg"
            className="rounded-full px-8 py-6 text-lg font-semibold bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25"
          >
            Start Your First Session
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#080b09] border-t border-gray-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Target className="w-4 h-4 text-[#111]" />
            </div>
            <span className="font-bold text-[#111]">GolfFlowPro</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <button onClick={() => navigate("/privacy")} className="text-[#555] hover:text-[#111] transition-colors">
              Privacy Policy
            </button>
            <button onClick={() => navigate("/terms")} className="text-[#555] hover:text-[#111] transition-colors">
              Terms
            </button>
          </div>
          <p className="text-[#555] text-sm">© 2025 GolfFlowPro</p>
        </div>
      </footer>

      {/* Auth Modal */}
      {renderAuthModal()}

      {/* Exit Popup */}
      <AnimatePresence>
        {showExitPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowExitPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white border border-gray-200 rounded-2xl p-8 max-w-md w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowExitPopup(false)}
                className="absolute top-4 right-4 text-[#555] hover:text-[#111]"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-emerald-700" />
                </div>
                <h3 className="text-2xl font-bold text-[#111] mb-2">
                  Wait! Free Session Inside
                </h3>
                <p className="text-[#555] mb-6">
                  Get your first <span className="text-emerald-700 font-semibold">personalized practice session</span> free. 
                  See what elite practice feels like.
                </p>
                
                <Button 
                  onClick={() => {
                    setShowExitPopup(false);
                    setAuthMode("register");
                  }}
                  className="w-full rounded-full py-6 bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  Get My Free Session
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                
                <button 
                  onClick={() => setShowExitPopup(false)}
                  className="text-sm text-[#555] mt-4 hover:text-[#555]"
                >
                  No thanks, I'll keep guessing
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
